import { Provider, useDispatch, useSelector } from "react-redux";
import Router from "./routes/Router";
import { store, persistor, clearPersistedData } from "./store/configureStore";
import { PersistGate } from "redux-persist/integration/react";
import axios from "axios";
import {
  deAuthenticate,
  setAuthenticationStatus,
} from "./store/reducers/authSlice";
import LoaderOverlay from "./components/Loader";
import ErrorModal from "./components/Alerts/ErrorModal/index";
import SuccessModal from "./components/Alerts/SuccessModal/index";
import SessionExpiredModal from "./components/Alerts/SessionExpiredModal";
import { ToastContainer } from "react-toastify";
import { useEffect } from "react";
import { PURGE_Brand } from "./store/reducers/brandReducer";
import UpdateDetector from "./components/UpdateDetector";
import { baseUrl } from "./utils/api/apis";

const REFRESH_URL = `${baseUrl}auth/refresh/`;

const logoutToSignin = () => {
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath.startsWith("/signin")) return;
  store.dispatch(deAuthenticate());
  store.dispatch(PURGE_Brand());
  localStorage.clear();
  sessionStorage.clear();
  sessionStorage.setItem("redirectAfterLogin", currentPath);
  window.location.href = "/signin";
  clearPersistedData();
};

let refreshPromise = null;
const refreshSession = () => {
  if (!refreshPromise) {
    const refresh_token = store.getState()?.auth?.refreshToken;
    if (!refresh_token) {
      return Promise.reject(new Error("No refresh token available"));
    }

    refreshPromise = axios
      .post(REFRESH_URL, { refresh_token })
      .then((res) => {
        const resData = res?.data?.data || res?.data || {};
        const idToken =
          resData.idToken ||
          resData.id_token ||
          resData.access_token ||
          resData.token ||
          resData.access;

        if (!idToken) throw new Error("No idToken in refresh response");

        const newRefreshToken =
          resData.refreshToken ||
          resData.refresh_token ||
          resData.refresh ||
          refresh_token;

        store.dispatch(
          setAuthenticationStatus({
            ...resData,
            idToken,
            refreshToken: newRefreshToken,
          }),
        );

        axios.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;
        return idToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config || {};
    const url = config.url || "";
    const isAuthUrl =
      url.includes("/auth/admin/login/") ||
      url.includes("/auth/admin/mfa/") ||
      url.includes("/user/profile/") ||
      url.includes("/auth/refresh/");

    if (isAuthUrl || (status !== 401 && status !== 403)) {
      return Promise.reject(error);
    }

    if (!config._retry && store.getState()?.auth?.refreshToken) {
      config._retry = true;
      try {
        const idToken = await refreshSession();
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${idToken}`,
        };
        return axios(config);
      } catch (e) {}
    }

    logoutToSignin();
    return Promise.reject(error);
  }
);

function App() {
  const dispatch = useDispatch();
  const ee_device_id = localStorage.getItem("ee-device-id");
  axios.defaults.headers.common["X-EE-Device-Id"] = ee_device_id;

  let ee_session_token = localStorage.getItem("ee-session-token");
  if (ee_session_token) {
    axios.defaults.headers.common["X-SESSION-TOKEN"] = ee_session_token;
  }

  // Proactively refresh token 5 minutes before the backend expiresIn (3600s / 1hr) expires
  const authState = useSelector((state) => state.auth);
  const tokenExpiresAt = authState?.tokenExpiresAt;
  const isAuthenticated = authState?.isAuthenticated;
  const refreshToken = authState?.refreshToken;

  useEffect(() => {
    if (!isAuthenticated || !refreshToken) return;

    // Buffer: refresh 5 minutes before the token expires
    const BUFFER_MS = 5 * 60 * 1000; // 5 minutes (300,000 ms)
    const targetExpiresAt = tokenExpiresAt || Date.now() + 3600 * 1000;
    const timeUntilRefresh = targetExpiresAt - Date.now() - BUFFER_MS;

    let timerId = null;

    if (timeUntilRefresh <= 0) {
      // Token is already near expiration, refresh immediately
      refreshSession().catch(() => {});
    } else {
      // Schedule refresh for 5 minutes before expiry (e.g. at 55 minutes)
      timerId = setTimeout(() => {
        refreshSession().catch(() => {});
      }, timeUntilRefresh);
    }

    // When returning to tab or waking computer from sleep, refresh if past the buffer threshold
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const remaining = targetExpiresAt - Date.now() - BUFFER_MS;
        if (remaining <= 0) {
          refreshSession().catch(() => {});
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (timerId) clearTimeout(timerId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, refreshToken, tokenExpiresAt]);

  useEffect(() => {
    const isProduction = window.location.hostname === "super.equityedge.co.uk";

    if (isProduction) {
      console.log = () => {}; // Silencing console logs in production
    }
  }, []);

  const selectedBrand = useSelector((state) => state.brand.selectedBrand);

  useEffect(() => {
    document.body.classList.remove(
      "equity_edge",
      "futures_edge",
      "crypto_edge"
    );
    document.body.classList.add(selectedBrand.brand); // Use `.brand`
  }, [selectedBrand.brand]); // Depend on `selectedBrand.brand`
  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove("equity_edge", "futures_edge", "crypto_edge");
    html.classList.add(selectedBrand.brand);
  }, [selectedBrand.brand]);
  return (
    <div
      className={`App ${
        selectedBrand.brand === "crypto_edge" && "crypto_edge"
      }`}
    >
      <ToastContainer />
      <UpdateDetector />
      <Router />
      <SuccessModal />
      <ErrorModal />
      <SessionExpiredModal />
    </div>
  );
}

export default App;
